<template>
  <ValidationObserver ref="validationObserver" v-slot="{ }">
    <div class="bundle-contents">
      <template v-if="bundleContentOwnerships.length">
        <div class="red f-instructions" v-if="bundle.hasActiveLicenses">{{ $t('This bundle has active licenses, so the contents cannot be changed.') }}</div>

        <div class="searchandfilter">
          <UIInput :placeholder="$t('Search for a Title')" type="search" v-model="search" class="search" />
        </div>

        <div class="searchtable">
          <div class="tableheader f6 storm">
            <div>{{ $t('Title is in Bundle') }}</div>
            <div>{{ $t('Current US price / month') }}</div>
            <div>{{ $t('Percentage Off') }}</div>
            <div>{{ $t('Bundle price / month') }}<br /><span style="color:red">{{ totalPricePerMonth() }}</span></div>
          </div>
          <template v-if="bundleContentOwnerships.length > 0 && bundle && bundle.bundleContents">
            <template v-if="contentOwnershipResults.length > 0">
              <div class="gameresult f6 storm" v-for="contentOwnership in contentOwnershipResults" :key="contentOwnership.id">
                <UISwitch
                  :disabled="bundle.hasActiveLicenses"
                  :title="contentOwnership.title"
                  :value="isContentOwnershipSelected(contentOwnership)"
                  @change="toggleContentOwnershipOnBundle(contentOwnership)"
                />
                <div>{{ currentPriceMonth(contentOwnership) }}</div>
                <UIInput
                  class="percentage"
                  :disabled="bundle.hasActiveLicenses"
                  v-if="isContentOwnershipSelected(contentOwnership)"
                  v-model="bundleContentByOwnership(contentOwnership).discountPercentage"
                  rules="required|decimal:2|minValue:0|maxValue:100"
                  type="number"
                  @change="validate"
                />
                <div v-if="isContentOwnershipSelected(contentOwnership)">${{ discountedPricePerMonth(contentOwnership) }}</div>
              </div>
            </template>
            <template v-else>
              <div class="empty noresults">
                <p>{{ $t("No content titles with active monthly prices could be found.") }}</p>
              </div>
            </template>
          </template>

          <div class="empty noresults f-body tc center fill-geyser fadeIn animated" v-else>
            <UIIcon name="no-results" class="dib mv5" style="width:8rem;" />
            <p>{{ $t("We couldn't find any contents matching your search.") }}</p>
          </div>
        </div>
      </template>
    </div>
  </ValidationObserver>
</template>

<script>
import Vue from 'vue';
import Component from 'vue-class-component';
import { ValidationObserver } from 'vee-validate';
import UIInput from '@/core/shared/components/ui/UIInput';
import UIIcon from '@/core/shared/components/ui/UIIcon';
import UISwitch from '@/core/shared/components/ui/UISwitch';

@Component({
  components: {
    UIInput,
    UIIcon,
    UISwitch,
    ValidationObserver,
  },
  props: {
    bundle: {
      type: Object,
      required: true,
    },
    bundleContentOwnerships: {
      type: Array,
      required: true,
    },
  },
  data: () => {
    return {
      search: '',
      deletedBundleContents: [],
    };
  },
  computed: {
    localBundle: function () {
      return new Proxy(this.bundle, {
        set: (target, property, value) => {
          this.$emit('edit', property, value);
          return true;
        },
      });
    },
  },
  methods: {
    async validate () {
      const valid = await this.$refs.validationObserver.validate();
      if (valid !== this.formIsValid) {
        this.formIsValid = valid;
        this.$emit('valid', valid);
      }
    },
  },
})
export default class SetupContents extends Vue {
  get contentOwnershipResults () {
    return this.bundleContentOwnerships.filter(x =>
      x.currentPrice != null &&
      x.currentPrice.priceMonth != null &&
      x.currentPrice.priceMonth > 0 &&
      (x.title.toLowerCase().indexOf(this.search.toLowerCase()) !== -1 ||
      this.bundle.bundleContents.some(c => c.contentOwnershipId === x.id)));
  }

  getBundleContentPriceMonth (index) {
    const content = this.bundle.bundleContents[index];
    if (content.contentOwnership && content.contentOwnership.prices && content.contentOwnership.prices.length > 0) {
      return Math.round(content.contentOwnership.prices[0].priceMonth) / 100;
    }
    return 0;
  }

  currentPriceMonth (contentOwnership) {
    if (contentOwnership.currentPrice.priceMonth) {
      return '$' + contentOwnership.currentPrice.priceMonth / 100;
    }
    return 0;
  }

  discountedPricePerMonth (contentOwnership) {
    const index = this.getContentIndexInBundle(contentOwnership);
    if (index !== -1) {
      let discountPercentage = this.bundle.bundleContents[index].discountPercentage;
      discountPercentage = discountPercentage == null ? 0 : discountPercentage;
      return Math.round((1 - (discountPercentage / 100)) * contentOwnership.currentPrice.priceMonth) / 100;
    }
    return 0;
  }

  formattedDiscountedPricePerMonth (contentOwnership) {
    return '$' + this.discountedPricePerMonth(contentOwnership);
  }

  totalPricePerMonth () {
    let total = 0;
    if (this.bundle && this.bundle.bundleContents) {
      this.bundleContentOwnerships.forEach(x => {
        const index = this.getContentIndexInBundle(x);
        if (index !== -1) {
          total += this.discountedPricePerMonth(x);
        }
      });
    }
    return '$' + Math.round(total * 100) / 100;
  }

  isContentOwnershipSelected (contentOwnership) {
    return ~this.getContentIndexInBundle(contentOwnership);
  };

  getContentIndexInBundle (contentOwnership) {
    if (this.bundle && this.bundle.bundleContents) {
      return this.bundle.bundleContents.findIndex(x => {
        return x.contentOwnershipId === contentOwnership.id;
      });
    }
    return [];
  }

  bundleContentByOwnership (contentOwnership) {
    const index = this.getContentIndexInBundle(contentOwnership);
    if (index !== -1) {
      return this.bundle.bundleContents[index];
    }
    return null;
  }

  toggleContentOwnershipOnBundle (contentOwnership) {
    const index = this.getContentIndexInBundle(contentOwnership);
    if (~index) {
      // Store deleted bundle contents, so we can put them back with all
      // related settings if they're needed after all
      this.deletedBundleContents.push(this.bundle.bundleContents[index]);

      const newArray = [
        ...this.bundle.bundleContents.slice(0, index),
        ...this.bundle.bundleContents.slice(index + 1),
      ];
      this.bundle.bundleContents = newArray;
    } else {
      if (!this.bundle.bundleContents) {
        this.bundle.bundleContents = [];
      }
      // Check if the title was previously deleted
      const deleted = this.deletedBundleContents.find(x => x.contentOwnershipId === contentOwnership.id);
      if (deleted) {
        // Move it back to the bundle if it was...
        const deletedIndex = this.deletedBundleContents.indexOf(deleted);
        this.localBundle.bundleContents.push(deleted);
        this.deletedBundleContents = [
          ...this.bundle.bundleContents.slice(0, deletedIndex),
          ...this.bundle.bundleContents.slice(deletedIndex + 1),
        ];
      } else {
        // ...else create a new entry
        this.localBundle.bundleContents.push({
          contentOwnershipId: contentOwnership.id,
        });
      }
    }
  }
};
</script>

<style lang="postcss" scoped>
@import "core/shared/styles";

.bundle-contents {
  display: grid;
  grid-gap: var(--spacingMd);

  & .searchandfilter {
    display: grid;
    grid-gap: var(--spacingMd);

    @media (--tablet) {
      grid-template-columns: 1fr 1fr 1fr 1fr;
      align-items: end;
    }

    & .search {
      grid-column: span 2;
    }
  }

  & .searchtable {
    display: grid;
    grid-gap: var(--spacingMd);

    & .tableheader,
    & .gameresult {
      display: grid;
      grid-template-columns: 1fr 1fr 1fr 1fr;
      align-items: center;

      & .tablet {
        display: none;
      }

      @media (--tablet) {
        grid-gap: var(--spacingMd);
        grid-template-columns: 1fr 1fr 1fr 1fr;

        & .tablet {
          display: flex;
        }
      }
    }

    & .tableheader {
      padding-bottom: var(--spacingSm);
      border-bottom: 0.1rem solid var(--colorStorm);
    }
  }
}
</style>
